package com.macrofocus.eflux.ui.layer

import com.macrofocus.common.units.Kilovolt
import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.deckgl.toJSonArray
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.network.substation.Substation
import com.macrofocus.eflux.ui.morphing.combinedMorphing
import deckgl.PolygonLayer
import js.objects.unsafeJso
import mui.material.styles.Theme
import org.locationtech.jts.geom.MultiPolygon
import org.locationtech.jts.geom.Polygon
import org.mkui.color.MkColor
import org.mkui.color.darker
import org.mkui.color.toDeckGL
import react.RefObject
import kotlin.js.Date

fun createInteractiveSubstationLayer(dataLoader: DataLoader,
                          substations: Array<Substation>?,
                          cartographic: Double,
                          lineWidth: Double,
                          theme: Theme,
                          isCartographicZoomed: Boolean,
                          probed: LogicalBusId?, selected: Array<LogicalBusId>,
                          lastClickTime: RefObject<Double>): PolygonLayer<Substation> {
    val controller = dataLoader.controller
    val doubleClickDelay = 300 // ms

    val substationLayer = PolygonLayer<Substation>(unsafeJso {
        id = "interactive-substation-layer"
        this.data = if (substations != null) substations else emptyArray<Array<Substation>>()

//        opacity = if(isCartographicZoomed) 0.05 else 1.0
        asDynamic().positionFormat = "XY"
        asDynamic().getFillColor = { d: Substation ->
            if (d.logicalBusId == probed)
                arrayOf(253, 255, 180, 153)
            else
                arrayOf(255, 255, 255, 0)
        }

        asDynamic().getPolygon = { d: Substation ->
            val physical = d.physicalGeometry
            val logical = d.logicalGeometry
//            val morphed: Array<Double>?
//            if (physical != null) {
//                morphed =
//                    morphCoordinates(logical.coordinate!!, physical.coordinate!!, cartographic, 10).toJSonArray()
//            } else {
//                if (cartographic <= 10 / 2) {
//                    morphed = logical.toJSonArray()
//                } else {
//                    morphed = emptyArray()
//                }
//            }
            val morphed: Array<Array<Double>>?
            if (logical is Polygon) {
                if (cartographic <= 0) {
                    morphed = logical.toJSonArray()
                } else {
                    if (physical is Polygon) {
                        if (cartographic >= 10) {
                            morphed = physical.toJSonArray()
                        } else {
                            if (d.logicalPolygon?.numPoints != d.physicalPolygon?.numPoints) {
                                //                                morphed = morphLinesParametric(logical, physical, cartographic, 10).toJSonArray()
                                println("This should never happen")
                                morphed = emptyArray<Array<Double>>()
                            } else {
                                morphed = combinedMorphing(
                                    d.logicalPolygon!!,
                                    d.physicalPolygon!!,
                                    cartographic,
                                    10
                                ).toJSonArray()
                            }
                        }
                        //   polygonToDeckGL(physical)
                    } else if (physical is MultiPolygon) {
                        //polygonToDeckGL(physical.getGeometryN(0) as Polygon)
                        //                        multiPolygonToDeckGL(physical)
                        //                        morphed = morphLinesParametric(logical, physical.getGeometryN(0) as Polygon, cartographic, 10).toJSonArray()
                        morphed =
                            combinedMorphing(
                                d.logicalPolygon!!,
                                d.physicalPolygon!!,
                                cartographic,
                                10
                            ).toJSonArray()
                    } else {
                        morphed = emptyArray<Array<Double>>()
                    }
                }
            } else {
                morphed = emptyArray<Array<Double>>()
            }
            morphed
        }

        asDynamic().stroked = true
        asDynamic().getLineColor = { d: Substation ->
            if (d.logicalBusId == probed)
                arrayOf(253, 255, 180, 153)
            else if (selected.contains(d.logicalBusId))
                arrayOf(253, 255, 180, 127)
            else
                arrayOf(255, 255, 255, 0)
        }
        asDynamic().getLineWidth = { d: Substation ->
            val w = if (d.type == "real")
                if(isCartographicZoomed) 2.0 else lineWidth * 0.25
            else 0.0
            w * 8.0
        }
        asDynamic().lineWidthUnits = "pixels"
        asDynamic().lineWidthMinPixels = 20
        asDynamic().lineWidthMaxPixels = 20
//        asDynamic().radiusScale = 3
        asDynamic().positionFormat = "XY"
        updateTriggers = unsafeJso {
            this.asDynamic().opacity = arrayOf(isCartographicZoomed)
            this.asDynamic().getPolygon = arrayOf(cartographic)
            this.asDynamic().getFillColor = arrayOf(theme, isCartographicZoomed)
            this.asDynamic().getLineColor = arrayOf(theme, probed, selected)
            this.asDynamic().getLineWidth = arrayOf(lineWidth, isCartographicZoomed, probed, selected)
            this.asDynamic().highlightColor = arrayOf(isCartographicZoomed)
//            this.asDynamic().lineWidthMinPixels = arrayOf(theme)
        }
        transitions = unsafeJso {
            this.asDynamic().getLineColor = 50
            this.asDynamic().getFillColor = 50
        }
        pickable = true
        autoHighlight = true
        highlightColor = if(isCartographicZoomed) arrayOf(253, 255, 180, 32) else arrayOf(253, 255, 180, 153)
        onHover = { info ->
            val o = info.`object`
            if (o != null) {
                if (o is Substation) {
                    val logicalBusId = o.logicalBusId
                    controller.probeSubstation(logicalBusId, true)
                } else {
                    controller.probeSubstation(null, true)
                }
            } else {
                controller.probeSubstation(null, true)
            }
        }
        onClick = { info: dynamic ->
            val now = Date.now()
            val dbl = now - lastClickTime.current!! < doubleClickDelay
            lastClickTime.current = now

            val o = info.`object`
            if (o != null) {
                if (o is Substation) {
                    if(dbl) {
                        dataLoader.physicalZoom(o.physicalGeometry)
                    }

                    val logicalBusId = o.logicalBusId
                    controller.selectSubstation(logicalBusId)
                } else {
                    controller.selectSubstation(null)
                }
            } else {
                controller.selectSubstation(null)
            }
        }
    })
    return substationLayer
}

fun createSubstationLayer(dataLoader: DataLoader,
                          substations: Array<Substation>?,
                          cartographic: Double,
                          lineWidth: Double,
                          theme: Theme,
                          isCartographicZoomed: Boolean,
                          probed: LogicalBusId?): PolygonLayer<Substation> {
    val kv380 = (theme.asDynamic().kv380Color) as MkColor
    val kv220 = (theme.asDynamic().kv220Color) as MkColor
    val kv150 = (theme.asDynamic().kv150Color) as MkColor
    val outOfService = (theme.asDynamic().outOfServiceColor) as MkColor
    val invalid = (theme.asDynamic().invalidColor) as MkColor
//    val kv380 = arrayOf(115, 70, 162)
//    val kv220 = arrayOf(23, 175, 151)
//    val kv150 = arrayOf(42, 156, 244)
//    val outOfService = arrayOf(188, 188, 188) // #bcbcbc
//    val invalid = arrayOf(188, 188, 188) // #bcbcbc

    val red = arrayOf(228, 3, 46) // #e4032e
    val orange = arrayOf(242, 130, 0) // #f28200
    val yellow = arrayOf(255, 204, 0) // #ffcc00
    val green = arrayOf(23, 175, 151) // #17af97

    val fill = theme.asDynamic().substationFill
    val stroke = (theme.asDynamic().substationColor) as MkColor

    val substationLayer = PolygonLayer<Substation>(unsafeJso {
        id = "substation-layer"
        this.data = if (substations != null) substations else emptyArray<Array<Substation>>()

        opacity = if(isCartographicZoomed) 0.05 else 1.0
        asDynamic().positionFormat = "XY"
        asDynamic().getFillColor = { d: Substation ->
            val baskv = d.baskv
            if(d.type == "real") {
                val vm = d.vm
                val voltage = if(baskv == null || vm == null) null else baskv.amount * vm.amount
                when {
                    voltage == null -> invalid.toDeckGL()
                    voltage == 0.0 -> outOfService.toDeckGL()
                    voltage > 421.0 -> red
                    voltage > 418.0 -> orange
                    voltage > 416.0 -> yellow
                    voltage > 399.0 -> fill
                    voltage > 397.0 -> yellow
                    voltage > 390.0 -> orange
                    voltage > 360.0 -> red
                    voltage > 246.0 -> red
                    voltage > 243.0 -> orange
                    voltage > 241.0 -> yellow
                    voltage > 229.0 -> fill
                    voltage > 227.0 -> yellow
                    voltage > 220.0 -> orange
                    voltage > 210.0 -> red
                    else -> fill
                }
            }
            else if(baskv == 380.Kilovolt)
                kv380.toDeckGL()
            else if(baskv == 220.Kilovolt)
                kv220.toDeckGL()
            else if(baskv == 150.Kilovolt)
                kv150.toDeckGL()
            else
                invalid.toDeckGL()

        }

        asDynamic().getPolygon = { d: Substation ->
            val physical = d.physicalGeometry
            val logical = d.logicalGeometry
//            val morphed: Array<Double>?
//            if (physical != null) {
//                morphed =
//                    morphCoordinates(logical.coordinate!!, physical.coordinate!!, cartographic, 10).toJSonArray()
//            } else {
//                if (cartographic <= 10 / 2) {
//                    morphed = logical.toJSonArray()
//                } else {
//                    morphed = emptyArray()
//                }
//            }
            val morphed: Array<Array<Double>>?
            if (logical is Polygon) {
                if (cartographic <= 0) {
                    morphed = d.logicalPolygonArray
                } else {
                    if (physical is Polygon) {
                        if (cartographic >= 10) {
                            morphed = d.physicalPolygonArray
                        } else {
                            if (d.logicalPolygon?.numPoints != d.physicalPolygon?.numPoints) {
                                //                                morphed = morphLinesParametric(logical, physical, cartographic, 10).toJSonArray()
                                println("This should never happen")
                                morphed = emptyArray<Array<Double>>()
                            } else {
                                morphed = combinedMorphing(
                                    d.logicalPolygon!!,
                                    d.physicalPolygon!!,
                                    cartographic,
                                    10
                                ).toJSonArray()
                            }
                        }
                        //   polygonToDeckGL(physical)
                    } else if (physical is MultiPolygon) {
                        //polygonToDeckGL(physical.getGeometryN(0) as Polygon)
                        //                        multiPolygonToDeckGL(physical)
                        //                        morphed = morphLinesParametric(logical, physical.getGeometryN(0) as Polygon, cartographic, 10).toJSonArray()
                        morphed =
                            combinedMorphing(
                                d.logicalPolygon!!,
                                d.physicalPolygon!!,
                                cartographic,
                                10
                            ).toJSonArray()
                    } else {
                        morphed = emptyArray<Array<Double>>()
                    }
                }
            } else {
                morphed = emptyArray<Array<Double>>()
            }
            morphed
        }

        asDynamic().stroked = true
        asDynamic().getLineColor = { d: Substation ->
//            if(d.type == "real") arrayOf(0, 0, 0) else arrayOf(0, 0, 0, 0)
            val c: MkColor = if(d.type == "real")
                stroke
            else if(d.baskv == 380.Kilovolt)
                kv380
            else if(d.baskv == 220.Kilovolt)
                kv220
            else if(d.baskv == 150.Kilovolt)
                kv150
            else
                invalid
            if(d.logicalBusId == probed) {
                c.darker().toDeckGL()
            } else {
                c.toDeckGL()
            }
        }
        asDynamic().getLineWidth = { d: Substation ->
            val w = if (d.type == "real")
                if(isCartographicZoomed) 2.0 else lineWidth * 0.25
            else 0.0
            if(d.logicalBusId == probed) {
                w * 2.0
            } else {
                w
            }
        }
        asDynamic().lineWidthUnits = "pixels"
        asDynamic().lineWidthMinPixels = 1.5
        asDynamic().lineWidthMaxPixels = 4
//        asDynamic().radiusScale = 3
        asDynamic().positionFormat = "XY"
        updateTriggers = unsafeJso {
            this.asDynamic().opacity = arrayOf(isCartographicZoomed)
            this.asDynamic().getPolygon = arrayOf(cartographic)
            this.asDynamic().getFillColor = arrayOf(theme, isCartographicZoomed)
            this.asDynamic().getLineColor = arrayOf(theme, probed)
            this.asDynamic().getLineWidth = arrayOf(lineWidth, isCartographicZoomed, probed)
            this.asDynamic().highlightColor = arrayOf(isCartographicZoomed)
//            this.asDynamic().lineWidthMinPixels = arrayOf(theme)
        }
        transitions = unsafeJso {
            this.asDynamic().getFillColor = 1000
        }
//        pickable = true
//        autoHighlight = true
//        highlightColor = if(isCartographicZoomed) arrayOf(253, 255, 180, 32) else arrayOf(253, 255, 180, 127)
//        onHover = { info ->
//            val o = info.`object`
//            if (o != null) {
//                if (o is Substation) {
//                    val logicalBusId = o.logicalBusId
//                    dataLoader.configuration.logicalBusProbing.selected = logicalBusId
//                } else {
//                    dataLoader.configuration.logicalBusProbing.clearSelection()
//                }
//            } else {
//                dataLoader.configuration.logicalBusProbing.clearSelection()
//            }
//        }
//        onClick = { info: dynamic ->
//            val o = info.`object`
//            if (o != null) {
//                if (o is Substation) {
//                    val logicalBusId = o.logicalBusId
//                    dataLoader.configuration.logicalBusSelection.setSelectedElement(logicalBusId)
//                } else {
//                    dataLoader.configuration.logicalBusSelection.clearSelection()
//                }
//            } else {
//                dataLoader.configuration.logicalBusSelection.clearSelection()
//            }
//        }
    })
    return substationLayer
}